import { RefObject, useEffect } from 'react';

import { getFocusableElements } from '@/hooks/getFocusableElements';

/**
 * While `isActive` is true, lets the user move focus between the focusable
 * descendants of `containerRef` using the Up/Down arrow keys (with Home/End
 * jumping to the first/last item). Focus wraps around at the ends.
 *
 * Intended for open menu/dropdown panels where arrow-key navigation is the
 * expected keyboard behaviour.
 */
export const useArrowKeyNavigation = (isActive: boolean, containerRef: RefObject<HTMLElement>): void => {
  useEffect(() => {
    if (!isActive) return undefined;

    const container = containerRef.current;

    if (!container) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

      const focusable = getFocusableElements(container);

      if (!focusable.length) return;

      event.preventDefault();
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      let nextIndex: number;

      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = focusable.length - 1;
      } else if (event.key === 'ArrowDown') {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % focusable.length;
      } else {
        nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
      }

      focusable[nextIndex]?.focus();
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive, containerRef]);
};
